<script lang="ts" setup>
import type { AppToggleSize } from './AppToggle.model';

export interface AppToggleProps {
  checked: boolean;
  disabled?: boolean;
  size?: AppToggleSize;
  labelPosition?: 'left' | 'right';
  ariaLabel?: string;
}

const props = withDefaults(defineProps<AppToggleProps>(), {
  size: 'l',
  labelPosition: 'left',
});

const emit = defineEmits<AppToggleEmits>();

interface AppToggleEmits {
  (event: 'toggle', checked: boolean): boolean;
}

function toggle(): void {
  if (props.disabled) {
    return;
  }
  emit('toggle', !props.checked);
}
</script>

<template>
  <div
    class="app-toggle"
    :class="[
      {
        disabled: props.disabled,
        'label-right': props.labelPosition === 'right',
      },
      [props.size],
    ]"
  >
    <div class="labels">
      <slot />
    </div>

    <button
      class="app-toggle-button"
      :class="{ checked: props.checked }"
      :aria-label="props.ariaLabel"
      @click="toggle"
    >
      <div class="app-toggle-button-handle" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
.app-toggle {
  --large-toggle-width: 60px;
  --large-toggle-height: 30px;
  --large-toggle-handle-size: 24px;
  --medium-toggle-width: 48px;
  --medium-toggle-height: 24px;
  --medium-toggle-handle-size: 18px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  &.label-right {
    flex-direction: row-reverse;
  }

  .labels {
    display: flex;
    flex-direction: column;
  }

  .app-toggle-button {
    position: relative;
    background-color: var(--color-neutral-30);
    border-radius: 15px;
    border-style: none;
    cursor: pointer;
    transition: background-color var(--transition-short);

    .app-toggle-button-handle {
      position: absolute;
      top: 50%;
      left: 5%;
      background-color: var(--color-white);
      border-radius: 50%;
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);
      transition: all var(--transition-short);
      transform: translate(0, -50%);
    }

    &.checked {
      background-color: var(--color-neutral-70);

      .app-toggle-button-handle {
        transform: translate(-100%, -50%);
        left: 95%;
      }
    }
  }

  &.disabled {
    opacity: 0.5;

    .app-toggle-button {
      cursor: default;
    }
  }

  &.l {
    .app-toggle-button {
      width: var(--large-toggle-width);
      height: var(--large-toggle-height);

      .app-toggle-button-handle {
        width: var(--large-toggle-handle-size);
        height: var(--large-toggle-handle-size);
      }
    }
  }

  &.m {
    .app-toggle-button {
      width: var(--medium-toggle-width);
      height: var(--medium-toggle-height);

      .app-toggle-button-handle {
        width: var(--medium-toggle-handle-size);
        height: var(--medium-toggle-handle-size);
      }
    }
  }
}
</style>
